"use client";

import {useOrg} from "@/hooks/useOrg";
import {apiLive} from "@/lib/api";
import {GAIL_API} from "@/utils/common/constants/routes";
import {createSharedApprovalLookup} from "./createSharedApprovalLookup";
import type {ApprovalReferenceKind} from "@/components/chat/approvalReferences";

type EntityKind = Exclude<ApprovalReferenceKind, "script" | "textScript" | "tool">;
const routes: Record<Exclude<EntityKind, "eval">, string> = {
   useCase: "/v1/use_cases", campaign: "/v1/campaigns", contact: "/v1/contacts", contactList: "/v1/contact_lists",
   sequence: "/v1/sequences", redialingRule: "/v1/redialing_rules", assistant: "/.internal/assistants", document: "/.internal/documents",
};
const text = (value: unknown) => typeof value === "string" && value.trim() ? value : undefined;
const useName = createSharedApprovalLookup(async key => {
   const [, kind, id, scriptId] = JSON.parse(key) as [string, EntityKind, string, string | null];
   const route = kind === "eval" ? `/v1/scripts/${encodeURIComponent(scriptId!)}/evals` : routes[kind];
   const value = await apiLive<Record<string, unknown>>(`${GAIL_API}${route}/${encodeURIComponent(id)}${kind === "assistant" ? "?includeArchived=true" : ""}`, undefined, undefined, undefined, true);
   if (!value) throw new Error("Reference name unavailable");
   if (typeof value.id === "string" && value.id.toLowerCase() !== id.toLowerCase()) return undefined;
   if (kind === "contact") return text([text(value.firstName), text(value.lastName)].filter(Boolean).join(" "));
   if (kind === "useCase") return text(value.title);
   if (kind === "document") return text(value.fileName);
   return text(value.name);
});

/** Deduplicate by organization, resource type, and ID; never keep another organization's labels. */
export function useApprovalEntityName(kind: EntityKind, id: string, scriptId?: string) {
   const {org} = useOrg();
   const scope = kind === "eval" ? scriptId : null;
   const key = org?.id && id && (kind !== "eval" || scope) ? JSON.stringify([org.id, kind, id, scope]) : null;
   return useName(key);
}
